/**
 * PhotoCredit — the credit a photo owes, drawn on the photo, for every source
 * (Wikimedia Commons, Google, Tiqets). Rules: docs/WIKIMEDIA_ATTRIBUTION.md.
 *
 * Pass the URL the <img> is showing RIGHT NOW (after any fallback or onError
 * swap). The credit is looked up from that URL (src/utils/photoCredit.js), so
 * it can't describe a different picture. Renders nothing for an image that
 * owes no credit (our users' photos, placeholders).
 *
 * Every photo carries its credit at every size (Kurtis, 2026-10-13) — tiny on
 * a thumbnail, but there. Pick `size` by the photo's rendered width:
 *   - 'lg' (≥ ~480px: heroes, lightbox, full-width banners)
 *       Photo: Ana Novak · CC BY-SA 4.0 · Wikimedia Commons — each part a link.
 *   - 'md' (~160–480px: cards)   © Ana Novak · CC BY-SA 4.0
 *   - 'sm' (~56–160px: rows, tiles)   © Ana Novak
 *   - 'xs' (< ~56px: pickers)   a © chip
 * md/sm/xs are one button that opens PhotoCreditSheet with the full credit
 * (title, author, license link, file page) — links in 9px text can't be hit.
 * The author truncates; the license never does.
 *
 * `position`:
 *   - 'bottom' (default) / 'top' — a strip along that edge on its own scrim;
 *     drop it inside the image's `relative` box.
 *   - 'corner' — bottom-right, no scrim (the xs chip's default).
 *   - 'inline' — no positioning, scrim or color: the caller places it (a line
 *     under a card's text, or inside an existing credit row).
 *
 * `showSource` (lg, default true) ends the line with the source's own link
 * ("Wikimedia Commons", "Google Maps"); a narrow host can drop it — the
 * author's link already opens the same page.
 * `showTitle` (lg) adds the file's title on a second line — pre-4.0 CC
 * licenses require the title; the lightbox shows it.
 * `onPhoto` (default true) shadows the text so it reads on any photo; pass
 * false for a credit line on a plain surface (a list row's caption).
 * `fallbackText` is shown when the URL is from no known source but the caller
 * holds a credit string for it (an event image's copyright line), and for a
 * Tiqets image, whose own credits can name the photographer.
 *
 * The row is pointer-events-none so a tap on the photo still lands (heroes
 * open a lightbox, cards navigate); only the credit's own button/links take
 * clicks, and they stop propagation. Don't render an interactive credit inside
 * an <a> or a <button>: put it beside the card's link (stretched-link
 * pattern). Where the photo is inside a row that IS a button (agenda rows,
 * pickers), pass `interactive={false}` — the same text, not tappable; the
 * row's tap opens the larger view, which carries the full credit.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePhotoCredit } from '../../utils/photoCredit';
import PhotoCreditSheet from './PhotoCreditSheet';

const stop = (e) => e.stopPropagation();

const POSITIONS = {
  bottom: 'absolute bottom-0 left-0 right-0 z-raised bg-gradient-to-t from-black/70 to-transparent text-content-inverse',
  top: 'absolute top-0 left-0 right-0 z-raised bg-gradient-to-b from-black/70 to-transparent text-content-inverse',
  corner: 'absolute bottom-1 right-1 z-raised text-content-inverse',
  inline: '',
};

// Strip padding per size: enough scrim behind white text, little photo lost.
const STRIP_PAD = {
  lg: { bottom: 'px-2 pb-1.5 pt-5', top: 'px-2 pt-1.5 pb-5' },
  md: { bottom: 'px-1.5 pb-1 pt-4', top: 'px-1.5 pt-1 pb-4' },
  sm: { bottom: 'px-1 pb-0.5 pt-3', top: 'px-1 pt-0.5 pb-3' },
  xs: { bottom: 'p-0.5', top: 'p-0.5' },
};

const TEXT = { lg: 'text-[11px]', md: 'text-[10px]', sm: 'text-[9px]', xs: 'text-[9px]' };

// White credit text has to read over a bright sky as well as a dark scrim
// (QA 2026-10-13: a cream façade washed a carousel credit out). text-shadow is
// inherited, so it reaches every span without touching the scrim background.
const ON_PHOTO = '[text-shadow:0_1px_2px_rgba(0,0,0,0.6)]';

function sourceLabel(t, source) {
  if (source === 'google') return t('photoCredit.googleMaps');
  if (source === 'tiqets') return t('photoCredit.tiqets');
  return t('photoCredit.commons');
}

/** The credit as one plain sentence, for aria labels. */
export function creditSentence(t, credit) {
  const parts = [credit.author || sourceLabel(t, credit.source)];
  if (credit.public_domain && !credit.license) parts.push(t('photoCredit.publicDomain'));
  if (credit.license) parts.push(credit.license);
  if (credit.author && credit.source !== 'tiqets') parts.push(sourceLabel(t, credit.source));
  return parts.join(', ');
}

function ExternalLink({ href, children, className = '', ariaLabel }) {
  if (!href) return <span className={className}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stop}
      aria-label={ariaLabel}
      className={`pointer-events-auto hover:underline ${className}`}
    >
      {children}
    </a>
  );
}

function Avatar({ credit, px }) {
  if (credit.source !== 'google' || !credit.author_photo_url) return null;
  return (
    <img
      src={credit.author_photo_url}
      alt=""
      referrerPolicy="no-referrer"
      className="rounded-full flex-shrink-0"
      style={{ width: px, height: px }}
    />
  );
}

// lg: the whole credit as links, no sheet (it sits on heroes and in the
// lightbox, which a modal can't open over).
function LargeCredit({ credit, showLabel, showSource, showTitle, t }) {
  const label = sourceLabel(t, credit.source);
  const licenseText = credit.license || (credit.public_domain ? t('photoCredit.publicDomain') : null);
  return (
    <span className="flex flex-col min-w-0">
      <span className="flex items-center gap-1 min-w-0">
        <Avatar credit={credit} px={16} />
        {showLabel && <span className="flex-shrink-0">{t('photoCredit.photoBy')}</span>}
        <ExternalLink
          href={credit.author_url || credit.source_url}
          className="font-medium truncate min-w-0"
          ariaLabel={t('photoCredit.linkAria', { label: credit.author || label })}
        >
          {credit.author || label}
        </ExternalLink>
        {licenseText && (
          <>
            <span aria-hidden="true" className="flex-shrink-0">·</span>
            <ExternalLink
              href={credit.license_url}
              className="flex-shrink-0 whitespace-nowrap"
              ariaLabel={credit.license_url ? t('photoCredit.licenseAria', { license: licenseText }) : undefined}
            >
              {licenseText}
            </ExternalLink>
          </>
        )}
        {showSource && credit.author && credit.source !== 'tiqets' && (
          <>
            <span aria-hidden="true" className="flex-shrink-0">·</span>
            <ExternalLink
              href={credit.source_url}
              className="flex-shrink-0 whitespace-nowrap"
              ariaLabel={t('photoCredit.linkAria', { label })}
            >
              {label}
            </ExternalLink>
          </>
        )}
      </span>
      {showTitle && credit.title && (
        <span className="truncate opacity-80">{credit.title}</span>
      )}
    </span>
  );
}

function compactText(t, credit, size) {
  const name = credit.author || sourceLabel(t, credit.source);
  // Commons asks re-users to "avoid the use of the © symbol" on public domain
  // work; a Tiqets or provisional credit names a source, not an owner.
  const mark = credit.public_domain || !credit.author || credit.source === 'tiqets' ? '' : '© ';
  if (size === 'xs') return { name: credit.public_domain ? 'PD' : '©', rest: null };
  if (size === 'sm') return { name: `${mark}${name}`, rest: null };
  const licenseText = credit.license || (credit.public_domain ? t('photoCredit.publicDomain') : null);
  return { name: `${mark}${name}`, rest: licenseText };
}

function PhotoCredit({
  src,
  size = 'md',
  position,
  showLabel = true,
  showTitle = false,
  showSource = true,
  fallbackText = null,
  interactive = true,
  onPhoto = true,
  className = '',
}) {
  const { t } = useTranslation();
  const credit = usePhotoCredit(src);
  const [open, setOpen] = useState(false);
  // The sheet lives only in the button branch. If the credit leaves that
  // branch (the slide goes off screen and turns non-interactive, the photo
  // changes) while open, close it — otherwise `open` stays true and the sheet
  // reappears untapped when the branch comes back.
  const sheetAvailable = !!credit && interactive && size !== 'lg'
    && !(credit.source === 'tiqets' && fallbackText);
  useEffect(() => {
    if (!sheetAvailable) setOpen(false);
  }, [sheetAvailable]);
  useEffect(() => {
    setOpen(false);
  }, [src]);
  const pos = position || (size === 'xs' ? 'corner' : 'bottom');
  const frame = `${POSITIONS[pos] ?? POSITIONS.bottom} ${STRIP_PAD[size]?.[pos] || ''}`;
  const base = `flex items-center min-w-0 leading-tight pointer-events-none ${TEXT[size] || TEXT.md} ${onPhoto ? ON_PHOTO : ''}`;

  // A Tiqets credit names no photographer; a caller's line ("Photo: Jane Roe /
  // Tiqets", from the image's own credits) is the better one.
  if (!credit || (credit.source === 'tiqets' && fallbackText)) {
    if (!fallbackText) return null;
    return (
      <div className={`${base} ${frame} ${className}`}>
        <span className="truncate" title={fallbackText}>{fallbackText}</span>
      </div>
    );
  }

  if (size === 'lg') {
    return (
      <div className={`${base} ${frame} ${className}`}>
        <LargeCredit credit={credit} showLabel={showLabel} showSource={showSource} showTitle={showTitle} t={t} />
      </div>
    );
  }

  const { name, rest } = compactText(t, credit, size);

  // Inside a row that is itself a <button> or <a> (a calendar agenda row, a
  // picker), a second button would be invalid HTML: the same text stays on the
  // photo, and the row's own tap opens the larger view, which carries the full
  // credit (lg).
  if (!interactive) {
    return (
      <div className={`${base} ${frame} ${className}`}>
        {size === 'xs' ? (
          <span
            role="img"
            aria-label={t('photoCredit.passiveAria', { credit: creditSentence(t, credit) })}
            className="flex items-center justify-center w-4 h-4 rounded-full bg-black/60 text-white font-semibold leading-none"
          >
            {name}
          </span>
        ) : (
          <span className="flex items-center gap-1 min-w-0 max-w-full" aria-label={t('photoCredit.passiveAria', { credit: creditSentence(t, credit) })} role="note">
            <Avatar credit={credit} px={size === 'md' ? 12 : 10} />
            <span className="truncate min-w-0" aria-hidden="true">{name}</span>
            {rest && (
              <span className="flex-shrink-0 whitespace-nowrap" aria-hidden="true">
                <span> · </span>{rest}
              </span>
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`${base} ${frame} ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        onPointerDown={stop}
        aria-label={t('photoCredit.openAria', { credit: creditSentence(t, credit) })}
        aria-haspopup="dialog"
        className={size === 'xs'
          ? 'pointer-events-auto flex items-center justify-center min-w-[24px] min-h-[24px]'
          : 'pointer-events-auto flex items-center gap-1 min-w-0 max-w-full text-left py-1 -my-1 hover:underline'}
      >
        {size === 'xs' ? (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-black/60 text-white font-semibold leading-none">
            {name}
          </span>
        ) : (
          <>
            <Avatar credit={credit} px={size === 'md' ? 12 : 10} />
            <span className="truncate min-w-0">{name}</span>
            {rest && (
              <span className="flex-shrink-0 whitespace-nowrap">
                <span aria-hidden="true"> · </span>{rest}
              </span>
            )}
          </>
        )}
      </button>
      {/* The sheet portals to document.body, but React still bubbles its
          events through THIS tree — into the card, carousel or drag handle the
          credit sits on. Without this wall a tap on the dimmed backdrop
          reached UnifiedCard's onClick and opened the detail page, and a drag
          on the sheet paged the carousel or started a dnd-kit drag behind it.
          Escape and Tab pass: Modal's close and focus trap listen on the
          document, and a React stopPropagation here would stop the native
          event before it got there (React 19 listens at the portal root). */}
      {open && (
        <div
          onClick={stop}
          onMouseDown={stop}
          onPointerDown={stop}
          onTouchStart={stop}
          onTouchMove={stop}
          onTouchEnd={stop}
          onKeyDown={(e) => { if (e.key !== 'Escape' && e.key !== 'Tab') e.stopPropagation(); }}
        >
          <PhotoCreditSheet credit={credit} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

export default PhotoCredit;
